import { captureSearchQuote, searchPayloadIdentity, SelectedTravelQuote } from './selectedQuote';
import { isQuoteFreshForHold } from './useFareCountdown';

export interface SearchResultGenerationInput {
  hasData: boolean;
  dataUpdatedAt: number | undefined;
  isFetching: boolean;
  isStale: boolean;
  /** El formulario ya no describe la búsqueda que produjo estos resultados. */
  formDiverged: boolean;
}

export interface SearchResultGenerationAuthority {
  canSelectResult: boolean;
  isLocallyExpired: boolean;
  isRefreshingStaleGeneration: boolean;
  isFormDiverged: boolean;
}

/**
 * ¿El formulario dejó de describir la búsqueda que produjo lo que se ve? UNA
 * definición para las nueve verticales (21.22): cada buscador proyecta su
 * formulario vivo al MISMO payload que arma desde la URL comprometida, y acá
 * se comparan por identidad normalizada. Lo que no cotiza (nombres para
 * repintar un autocomplete, filtros y orden client-side) queda afuera por
 * construcción: no forma parte del payload.
 *
 * Sin búsqueda comprometida no hay divergencia posible — no hay resultados de
 * los que estos campos puedan diferir. Un formulario que YA NO es ejecutable
 * (un campo requerido borrado) sí diverge: los resultados en pantalla siguen
 * siendo de la búsqueda anterior.
 */
export function searchFormDiverged(
  committedPayload: unknown | null | undefined,
  draftPayload: unknown | null | undefined,
): boolean {
  if (committedPayload === null || committedPayload === undefined) return false;
  if (draftPayload === null || draftPayload === undefined) return true;
  return searchPayloadIdentity(committedPayload) !== searchPayloadIdentity(draftPayload);
}

/** Local authority of the one cached generation currently shown by a search. */
export function searchResultGenerationAuthorityAt(
  input: SearchResultGenerationInput,
  now: number,
): SearchResultGenerationAuthority {
  const isFresh = input.hasData && isQuoteFreshForHold(input.dataUpdatedAt, now);
  // Divergir es una propiedad de los resultados EN PANTALLA: sin generación
  // que contradecir, un formulario a medio escribir no marca nada.
  const isFormDiverged = input.hasData && input.formDiverged;

  return {
    canSelectResult: isFresh && !isFormDiverged,
    isLocallyExpired: input.hasData && !isFresh,
    isRefreshingStaleGeneration: input.hasData && input.isStale && input.isFetching,
    isFormDiverged,
  };
}

/**
 * Por qué está bloqueada la selección. Vencido y divergido son estados
 * DISTINTOS (21.18.4), no un booleano común: se dicen con carteles distintos.
 */
type SearchSelectionBlock = 'formDiverged' | 'expired';

/**
 * El criterio ÚNICO de precedencia: divergir manda sobre vencer, porque es lo
 * que el asesor acaba de provocar y lo que su próxima acción resuelve
 * (ejecutar la búsqueda que ya escribió). El aviso de arriba de la lista y el
 * motivo pegado al botón salen de ESTA función, no de dos condiciones
 * parecidas escritas en dos JSX distintos (21.9).
 */
function searchSelectionBlock(authority: SearchResultGenerationAuthority): SearchSelectionBlock | undefined {
  if (authority.isFormDiverged) return 'formDiverged';
  if (authority.isLocallyExpired) return 'expired';
  return undefined;
}

/** El aviso completo, arriba de la lista. */
const BLOCK_NOTICE_KEY: Record<SearchSelectionBlock, string> = {
  formDiverged: 'travelServices.formDiverged',
  expired: 'travelServices.resultsExpired',
};

/** La versión corta, donde solo entra un renglón al lado del botón. */
const BLOCK_SHORT_KEY: Record<SearchSelectionBlock, string> = {
  formDiverged: 'travelServices.formDivergedShort',
  expired: 'travelServices.resultsExpiredShort',
};

export function searchSelectionBlockNoticeKey(authority: SearchResultGenerationAuthority): string | undefined {
  const block = searchSelectionBlock(authority);
  return block && BLOCK_NOTICE_KEY[block];
}

export function searchSelectionBlockedReasonKey(authority: SearchResultGenerationAuthority): string | undefined {
  const block = searchSelectionBlock(authority);
  return block && BLOCK_SHORT_KEY[block];
}

/**
 * Event-time selection boundary. A render that was fresh one tick ago cannot
 * open a booking flow after the generation reaches its local TTL, and a form
 * that no longer describes the committed search cannot open one at all.
 */
export function captureSelectableSearchQuote<TOffer>(
  offer: TOffer,
  dataUpdatedAt: number | undefined,
  vertical: string,
  agencyId: string | number,
  committedPayload: unknown | null,
  now: number,
  formDiverged: boolean,
): SelectedTravelQuote<TOffer> | null {
  if (formDiverged) return null;
  if (!isQuoteFreshForHold(dataUpdatedAt, now)) return null;
  return captureSearchQuote(offer, dataUpdatedAt, vertical, agencyId, committedPayload);
}

/** Search on an expired identical key must explicitly produce a new generation. */
export function shouldRefetchExpiredIdenticalSearch(
  currentSearchIdentity: string | undefined,
  nextSearchIdentity: string | undefined,
  hasData: boolean,
  dataUpdatedAt: number | undefined,
  now: number,
): boolean {
  return Boolean(
    hasData &&
      !isQuoteFreshForHold(dataUpdatedAt, now) &&
      currentSearchIdentity &&
      nextSearchIdentity &&
      currentSearchIdentity === nextSearchIdentity,
  );
}
